import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { categories, hotRanking, latestUpdates, recommendedComics } from "@/mocks/comics";

const categoryComicsMap: Record<string, typeof hotRanking> = {
  romance: [...hotRanking.filter((c) => c.genre === "恋爱漫画"), ...recommendedComics.filter((c) => c.genre === "恋爱漫画").map((c, i) => ({ rank: i + 20, title: c.title, author: c.author, genre: c.genre, views: "5000万", rating: 8.8, cover: c.cover, slug: c.slug }))],
  fantasy: [...hotRanking.filter((c) => c.genre === "奇幻冒险"), ...recommendedComics.filter((c) => c.genre === "奇幻冒险").map((c, i) => ({ rank: i + 30, title: c.title, author: c.author, genre: c.genre, views: "4000万", rating: 8.7, cover: c.cover, slug: c.slug }))],
  action: [...hotRanking.filter((c) => c.genre === "热血动作"), ...latestUpdates.slice(0, 6).map((c, i) => ({ rank: i + 40, title: c.title, author: "佚名", genre: "热血动作", views: "3000万", rating: 8.5, cover: c.cover, slug: c.slug }))],
  drama: hotRanking.filter((c) => c.genre === "都市剧情").length > 0 ? hotRanking.filter((c) => c.genre === "都市剧情") : [...latestUpdates.slice(4, 10).map((c, i) => ({ rank: i + 50, title: c.title, author: "佚名", genre: "都市剧情", views: "2000万", rating: 8.3, cover: c.cover, slug: c.slug }))],
  thriller: hotRanking.filter((c) => c.genre === "悬疑惊悚"),
  comedy: hotRanking.filter((c) => c.genre === "搞笑日常").length > 0 ? hotRanking.filter((c) => c.genre === "搞笑日常") : latestUpdates.slice(2, 8).map((c, i) => ({ rank: i + 60, title: c.title, author: "佚名", genre: "搞笑日常", views: "1500万", rating: 8.2, cover: c.cover, slug: c.slug })),
  historical: hotRanking.filter((c) => c.genre === "古风历史"),
  bl: hotRanking.filter((c) => c.genre === "BL漫画"),
  gl: hotRanking.filter((c) => c.genre === "GL漫画").length > 0 ? hotRanking.filter((c) => c.genre === "GL漫画") : latestUpdates.slice(6, 12).map((c, i) => ({ rank: i + 70, title: c.title, author: "佚名", genre: "GL漫画", views: "800万", rating: 8.1, cover: c.cover, slug: c.slug })),
  completed: hotRanking.slice(0, 6),
};

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState(slug || "romance");

  useEffect(() => {
    if (slug) setActiveCategory(slug);
  }, [slug]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [activeCategory]);

  const currentCat = categories.find((c) => c.slug === activeCategory);
  const comics = categoryComicsMap[activeCategory] || [];

  return (
    <>
      <Navbar />
      <main className="pt-16">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-8">
          <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-6">
            <Link to="/" className="hover:text-primary-500 transition-colors whitespace-nowrap">
              首页
            </Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-foreground-800 font-medium whitespace-nowrap">
              {currentCat?.name || "分类"}
            </span>
          </nav>

          <h1
            className="text-2xl md:text-3xl font-bold text-foreground-950 mb-8"
            style={{ fontFamily: "var(--font-heading)" }}
          >
            {currentCat?.name || "分类浏览"}
          </h1>

          <div className="flex flex-wrap gap-2 mb-8">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                onClick={() => setActiveCategory(cat.slug)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                  activeCategory === cat.slug
                    ? "bg-primary-500 text-background-50"
                    : "bg-background-100 text-foreground-600 hover:bg-background-200"
                }`}
              >
                {cat.name}
              </Link>
            ))}
          </div>

          {comics.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-5">
              {comics.map((comic, idx) => (
                <Link
                  key={comic.rank}
                  to={`/comic/${comic.slug}`}
                  className="group"
                >
                  <div className="relative aspect-[3/4] rounded-xl overflow-hidden mb-3">
                    <img
                      src={comic.cover}
                      alt={comic.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      title={`${comic.title} - 好看的韩国漫画`}
                    />
                    <div className="absolute top-2 left-2 w-6 h-6 rounded-lg bg-accent-500 flex items-center justify-center">
                      <span className="text-xs font-bold text-background-50">{idx + 1}</span>
                    </div>
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-900 line-clamp-1 group-hover:text-primary-500 transition-colors">
                    {comic.title}
                  </h3>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-xs text-foreground-500">{comic.author}</span>
                  </div>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="flex items-center gap-0.5 text-xs text-accent-600">
                      <i className="ri-star-fill text-[10px]"></i> {comic.rating}
                    </span>
                    <span className="text-xs text-foreground-400">{comic.views}</span>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-background-100 flex items-center justify-center">
                <i className="ri-book-open-line text-2xl text-foreground-400"></i>
              </div>
              <p className="text-foreground-500">该分类暂无漫画</p>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}